import axios from "axios";
import { useEffect, useState } from "react";
import { ClipboardCheck, FileText, Truck, CheckCircle2 } from "lucide-react";
import { API, Modal, abs } from "./shared";
import { useEwayBill } from "./EwayBill";

export function ShiprocketShipmentPanel({ shiprocket, hideLabel }) {
  const [copied, setCopied] = useState(false);
  const awb = shiprocket?.awb;
  const label = abs(shiprocket?.label_url);
  const courier = shiprocket?.courier_name;
  const shipmentId = shiprocket?.shipment_id;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(awb);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <div
      data-testid="shiprocket-shipment-panel"
      style={{
        marginTop:10,marginBottom:12,
        padding:"14px 16px",
        background:"linear-gradient(135deg,#F0F9FF 0%,#EFF6FF 100%)",
        border:"1px solid #BFDBFE",borderRadius:12,
      }}
    >
      {shiprocket?.simulated && (
        <div data-testid="shiprocket-test-banner" style={{marginBottom:10,padding:"8px 10px",borderRadius:8,background:"#FEF3C7",border:"1px solid #FCD34D",color:"#92400E",fontSize:12,fontWeight:600}}>
          TEST shipment: nothing was booked with Shiprocket ({shiprocket.reason}). No courier will pick this up.
        </div>
      )}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",padding:"3px 7px",borderRadius:4,background:"#DCFCE7",color:"#166534"}}>BLIND LABEL</span>
        <div style={{fontSize:12,fontWeight:700,color:"#0F172A"}}>Shiprocket shipment</div>
        <span style={{marginLeft:"auto",fontSize:10.5,color:"#64748B"}}>Sender hidden · GoPrintPack brand only</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <div style={{fontSize:10,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.05em"}}>AWB</div>
          <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
            <code data-testid="shiprocket-awb" style={{fontSize:13,fontWeight:700,color:"#0F172A",fontFamily:"ui-monospace,Menlo,monospace"}}>{awb}</code>
            <button
              type="button"
              data-testid="shiprocket-copy-awb"
              onClick={copy}
              title="Copy AWB"
              style={{
                padding:"4px 8px",fontSize:10.5,fontWeight:700,
                color: copied ? "#166534" : "#1E3A8A",
                background: copied ? "#DCFCE7" : "#fff",
                border:`1px solid ${copied ? "#86EFAC" : "#BFDBFE"}`,
                borderRadius:6,cursor:"pointer",transition:"all 0.15s",
              }}
            >
              {copied ? "COPIED ✓" : "COPY"}
            </button>
          </div>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.05em"}}>Courier</div>
          <div data-testid="shiprocket-courier" style={{fontSize:13,fontWeight:600,color:"#0F172A",marginTop:2}}>{courier || "—"}</div>
          {shiprocket?.pickup_location && (
            <div data-testid="shiprocket-pickup-hub" style={{fontSize:10.5,color:"#64748B",marginTop:2}}>Hub: <b style={{color:"#0369A1"}}>{shiprocket.pickup_location}</b></div>
          )}
        </div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {hideLabel ? null : label ? (
          <a
            href={label}
            target="_blank"
            rel="noreferrer"
            data-testid="shiprocket-label-link"
            style={{
              display:"inline-flex",alignItems:"center",gap:6,
              padding:"8px 14px",fontSize:12,fontWeight:700,
              color:"#fff",background:"#2563EB",borderRadius:8,
              textDecoration:"none",transition:"all 0.15s",
            }}
          >
            <FileText size={13}/> {shiprocket?.simulated ? "Open test label" : "Download blind label (PDF)"}
          </a>
        ) : (
          <span data-testid="shiprocket-label-pending" style={{fontSize:11.5,color:"#92400E",background:"#FEF3C7",border:"1px solid #FDE68A",padding:"7px 10px",borderRadius:6}}>
            Label generating… refresh in a moment
          </span>
        )}
        {shipmentId && (
          <span data-testid="shiprocket-shipment-id" style={{fontSize:10.5,color:"#64748B",padding:"8px 10px"}}>
            Shipment ID: <code style={{fontFamily:"ui-monospace,Menlo,monospace"}}>{shipmentId}</code>
          </span>
        )}
      </div>
      <div style={{marginTop:10,fontSize:10.5,color:"#475569",lineHeight:1.5,padding:"8px 10px",background:"rgba(255,255,255,0.6)",borderRadius:6}}>
        <b>Warehouse:</b> Print this PDF and affix on the parcel. The sender block carries only GoPrintPack branding — your supplier identity never appears.
      </div>
    </div>
  );
}

export function DispatchModal({ job, close, refresh }) {
  const addr = job.delivery_address;
  const quoted = job.price_breakdown?.delivery || {};
  const quotedCourierId = quoted.courier_company_id;
  const locked = !!addr;
  const [mode, setMode] = useState(locked ? "shiprocket" : "manual"); // 'manual' | 'shiprocket'
  const [srStatus, setSrStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ewb] = useEwayBill(job.id);
  const ewbBlocked = !!(ewb?.required && !ewb?.ewaybill);
  const [manual, setManual] = useState({ tracking_number: "", carrier: "UPS" });
  const [sr, setSr] = useState({
    ship_customer_name: addr?.name || "",
    ship_customer_phone: addr?.phone || "",
    ship_customer_email: "",
    ship_address: addr?.address || "",
    ship_address_2: addr?.address_2 || "",
    ship_city: addr?.city || "",
    ship_state: addr?.state || "",
    ship_pincode: addr?.pincode || "",
    ship_weight_kg: job.price_breakdown?.delivery?.weight_kg ? String(job.price_breakdown.delivery.weight_kg) : "",
    ship_length_cm: "",
    ship_breadth_cm: "",
    ship_height_cm: "",
  });
  // Serviceability pre-check state. `step` controls which stage the form is in:
  //   'form'     — collecting recipient + parcel fields
  //   'checked'  — courier options loaded, user selects one, then submits
  const [step, setStep] = useState("form");
  const [checking, setChecking] = useState(false);
  const [courierOptions, setCourierOptions] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shiprocket/config`)
      .then(r => setSrStatus(r.data))
      .catch(() => setSrStatus({ configured: null }));
  }, []);

  // When any ship_* field changes, invalidate the previous courier pre-check.
  // Otherwise a user could edit the pincode after checking and submit against
  // couriers that no longer serve the new route.
  useEffect(() => {
    if (step === "checked") {
      setStep("form");
      setCourierOptions([]);
      setSelectedCourier(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sr.ship_pincode, sr.ship_weight_kg, sr.ship_length_cm, sr.ship_breadth_cm, sr.ship_height_cm]);

  const requiredFields = ["ship_customer_name","ship_customer_phone","ship_address","ship_city","ship_state","ship_pincode","ship_weight_kg","ship_length_cm","ship_breadth_cm","ship_height_cm"];
  const missingFields = () => requiredFields.filter(k => !String(sr[k] || "").trim());

  const checkServiceability = async () => {
    setError("");
    const missing = missingFields();
    if (missing.length) {
      setError(`Fill all required fields: ${missing.map(k => k.replace("ship_","")).join(", ")}`);
      return;
    }
    setChecking(true);
    try {
      const r = await axios.post(`${API}/shiprocket/serviceability`, {
        delivery_pincode: sr.ship_pincode.trim(),
        weight_kg: parseFloat(sr.ship_weight_kg),
      });
      const opts = r.data?.options || [];
      if (opts.length === 0) {
        setError("Shiprocket has no courier for this route today. Try a different pincode, or switch to Manual tracking and dispatch via another courier.");
        setStep("form");
        setCourierOptions([]);
        return;
      }
      setCourierOptions(opts);
      // Prefer the courier quoted & escrowed at checkout; else cheapest (opts sorted by rate).
      const best = opts.find(o => quotedCourierId && String(o.courier_company_id) === String(quotedCourierId)) || opts[0];
      setSelectedCourier({
        key: `${best.courier_company_id}:${best.pickup_location || ""}`,
        courier_company_id: best.courier_company_id,
        pickup_location: best.pickup_location,
        courier_name: best.courier_name,
        rate: best.rate,
      });
      setStep("checked");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Serviceability check failed");
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const labelWin = mode === "shiprocket" ? window.open("", "_blank") : null;
    try {
      let payload;
      if (mode === "manual") {
        if (!manual.tracking_number.trim()) throw new Error("Tracking number is required for manual dispatch");
        payload = { ...manual, use_shiprocket: false };
      } else {
        if (step !== "checked" || !selectedCourier) {
          throw new Error("Check courier options first, then pick a courier before dispatching");
        }
        payload = {
          use_shiprocket: true,
          shiprocket_courier_id: selectedCourier.courier_company_id,
          shiprocket_pickup_location: selectedCourier.pickup_location,
          ...sr,
          ship_weight_kg: parseFloat(sr.ship_weight_kg),
          ship_length_cm: parseFloat(sr.ship_length_cm),
          ship_breadth_cm: parseFloat(sr.ship_breadth_cm),
          ship_height_cm: parseFloat(sr.ship_height_cm),
        };
      }
      await axios.post(`${API}/jobs/${job.id}/dispatch`, payload);
      if (labelWin) labelWin.location.href = `${API}/jobs/${job.id}/blind-label`;
      await refresh();
      close();
    } catch (err) {
      if (labelWin) labelWin.close();
      setError(err?.response?.data?.detail || err?.message || "Dispatch failed");
    } finally {
      setSubmitting(false);
    }
  };

  const escrowedRate = Number(job.price_breakdown?.delivery?.carrier_rate_inr || 0);
  const lockedInput = locked ? { readOnly: true, style: { background: "#F1F5F9", color: "#475569", cursor: "not-allowed" } } : {};
  const shiprocketReady = srStatus?.configured === true;
  const pickupName = srStatus?.pickup_location;
  const pickupHubs = srStatus?.pickup_hubs || [];

  return (
    <Modal title="Mark job as dispatched" close={close}>
      <div data-testid="dispatch-mode-toggle" style={{display:"flex",gap:8,marginBottom:16,padding:4,background:"#F1F5F9",borderRadius:10}}>
        <button type="button" data-testid="dispatch-mode-manual" onClick={() => { setMode("manual"); setError(""); }}
          style={{flex:1,padding:"10px 12px",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13,
            background: mode === "manual" ? "#fff" : "transparent",
            color: mode === "manual" ? "#0F172A" : "#64748B",
            boxShadow: mode === "manual" ? "0 1px 2px rgba(15,23,42,0.08)" : "none"}}>
          Manual tracking
        </button>
        <button type="button" data-testid="dispatch-mode-shiprocket" onClick={() => { setMode("shiprocket"); setError(""); }}
          style={{flex:1,padding:"10px 12px",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13,
            background: mode === "shiprocket" ? "#fff" : "transparent",
            color: mode === "shiprocket" ? "#0F172A" : "#64748B",
            boxShadow: mode === "shiprocket" ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
            display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          Ship via Shiprocket
          <span style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.06em",padding:"2px 6px",borderRadius:4,
            background: shiprocketReady ? "#DCFCE7" : (srStatus === null ? "#F1F5F9" : "#FEF3C7"),
            color: shiprocketReady ? "#166534" : (srStatus === null ? "#64748B" : "#92400E")}}>
            {srStatus === null ? "…" : shiprocketReady ? "BLIND LABEL" : "SETUP"}
          </span>
        </button>
      </div>

      {mode === "shiprocket" && shiprocketReady && (
        <div data-testid="dispatch-shiprocket-info" style={{marginBottom:14,padding:"10px 12px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,fontSize:11.5,lineHeight:1.5,color:"#1E3A8A"}}>
          <b>Blind-shipping enabled.</b>{" "}
          {pickupHubs.length > 1
            ? <>You have <b>{pickupHubs.length} pickup hubs</b> ({pickupHubs.map(h => h.name).join(", ")}) — we'll check courier rates from each and auto-pick the cheapest route. Your business name, address, and contact never appear on the parcel.</>
            : <>The label will show <b>{pickupName}</b> as sender under the GoPrintPack brand — your business name, address, and contact never appear on the parcel.</>}
        </div>
      )}

      {mode === "shiprocket" && srStatus && !shiprocketReady && (
        <div data-testid="dispatch-shiprocket-unavailable" style={{marginBottom:14,padding:"10px 12px",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8,fontSize:11.5,lineHeight:1.5,color:"#92400E"}}>
          Shiprocket is not configured on this environment yet. Switch to <b>Manual tracking</b>.
        </div>
      )}

      <form onSubmit={submit} className="form-grid">
        {mode === "manual" ? (
          <>
            <label>Tracking number
              <input data-testid="dispatch-tracking-input" value={manual.tracking_number} onChange={e => setManual({ ...manual, tracking_number: e.target.value })} required/>
            </label>
            <label>Carrier
              <select data-testid="dispatch-carrier-select" value={manual.carrier} onChange={e => setManual({ ...manual, carrier: e.target.value })}>
                <option>UPS</option><option>FedEx</option><option>USPS</option><option>DHL</option>
              </select>
            </label>
          </>
        ) : (
          <>
            <div style={{gridColumn:"1 / -1",fontSize:11,fontWeight:800,color:"#334155",textTransform:"uppercase",letterSpacing:"0.08em",marginTop:4}}>Recipient (end buyer)</div>
            {locked && (
              <div data-testid="sr-address-locked" style={{gridColumn:"1 / -1",fontSize:11.5,color:"#065F46",background:"#ECFDF5",border:"1px solid #A7F3D0",borderRadius:8,padding:"8px 10px"}}>
                Delivery address captured at buyer checkout — locked to match the escrowed delivery charge. Buyer name, phone and email are hidden; GoPrintPack shares them directly with the courier.
              </div>
            )}
            <label>Full name
              <input data-testid="sr-ship-name" {...lockedInput} value={sr.ship_customer_name} onChange={e => setSr({...sr, ship_customer_name: e.target.value})} required />
            </label>
            <label>Phone (+91 format)
              <input data-testid="sr-ship-phone" {...lockedInput} placeholder="+919876543210" value={sr.ship_customer_phone} onChange={e => setSr({...sr, ship_customer_phone: e.target.value})} required />
            </label>
            {!locked && (
              <label style={{gridColumn:"1 / -1"}}>Email <span style={{color:"#94A3B8",fontWeight:500}}>(optional)</span>
                <input data-testid="sr-ship-email" type="email" value={sr.ship_customer_email} onChange={e => setSr({...sr, ship_customer_email: e.target.value})} />
              </label>
            )}
            <label style={{gridColumn:"1 / -1"}}>Street address
              <input data-testid="sr-ship-address" {...lockedInput} value={sr.ship_address} onChange={e => setSr({...sr, ship_address: e.target.value})} required />
            </label>
            <label style={{gridColumn:"1 / -1"}}>Address line 2 <span style={{color:"#94A3B8",fontWeight:500}}>(optional)</span>
              <input data-testid="sr-ship-address2" {...lockedInput} value={sr.ship_address_2} onChange={e => setSr({...sr, ship_address_2: e.target.value})} />
            </label>
            <label>City
              <input data-testid="sr-ship-city" {...lockedInput} value={sr.ship_city} onChange={e => setSr({...sr, ship_city: e.target.value})} required />
            </label>
            <label>State
              <input data-testid="sr-ship-state" {...lockedInput} value={sr.ship_state} onChange={e => setSr({...sr, ship_state: e.target.value})} required />
            </label>
            <label>Pincode
              <input data-testid="sr-ship-pincode" {...lockedInput} value={sr.ship_pincode} onChange={e => setSr({...sr, ship_pincode: e.target.value})} required />
            </label>

            <div style={{gridColumn:"1 / -1",fontSize:11,fontWeight:800,color:"#334155",textTransform:"uppercase",letterSpacing:"0.08em",marginTop:10}}>Parcel dimensions</div>
            <label>Weight (kg)
              <input data-testid="sr-weight" type="number" step="0.01" min="0.01" value={sr.ship_weight_kg} onChange={e => setSr({...sr, ship_weight_kg: e.target.value})} required />
            </label>
            <label>Length (cm)
              <input data-testid="sr-length" type="number" step="0.1" min="1" value={sr.ship_length_cm} onChange={e => setSr({...sr, ship_length_cm: e.target.value})} required />
            </label>
            <label>Breadth (cm)
              <input data-testid="sr-breadth" type="number" step="0.1" min="1" value={sr.ship_breadth_cm} onChange={e => setSr({...sr, ship_breadth_cm: e.target.value})} required />
            </label>
            <label>Height (cm)
              <input data-testid="sr-height" type="number" step="0.1" min="1" value={sr.ship_height_cm} onChange={e => setSr({...sr, ship_height_cm: e.target.value})} required />
            </label>

            {step === "checked" && courierOptions.length > 0 && (
              <div data-testid="sr-courier-options" style={{gridColumn:"1 / -1",marginTop:10,padding:"12px 14px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:10}}>
                <div style={{fontSize:11,fontWeight:800,color:"#334155",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                  <Truck size={13}/> Available couriers · pick one
                  <span style={{marginLeft:"auto",fontSize:10,fontWeight:600,color:"#64748B",textTransform:"none",letterSpacing:"normal"}}>Live from Shiprocket</span>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:260,overflowY:"auto"}}>
                  {courierOptions.map((o, idx) => {
                    const key = `${o.courier_company_id}:${o.pickup_location || ""}`;
                    const selectedKey = selectedCourier?.key;
                    const selected = key === selectedKey;
                    return (
                      <label key={key}
                        data-testid={`sr-courier-row-${o.courier_company_id}-${(o.pickup_location || "default").replace(/\s+/g,"_")}`}
                        style={{display:"flex",flexDirection:"row",alignItems:"center",textAlign:"left",textTransform:"none",font:"inherit",gap:10,padding:"10px 12px",background:selected?"#EFF6FF":"#fff",border:`1px solid ${selected?"#3B82F6":"#E2E8F0"}`,borderRadius:8,cursor:"pointer",transition:"all 0.15s"}}>
                        <input type="radio" name="sr-courier" checked={selected}
                          onChange={() => setSelectedCourier({ key, courier_company_id: o.courier_company_id, pickup_location: o.pickup_location, courier_name: o.courier_name, rate: o.rate })}
                          data-testid={`sr-courier-radio-${idx}`}
                          style={{margin:0}} />
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                            <span style={{fontWeight:700,color:"#0F172A",fontSize:13}}>{o.courier_name}</span>
                            {quotedCourierId && String(o.courier_company_id) === String(quotedCourierId) && (
                              <span data-testid={`sr-quoted-badge-${idx}`} style={{fontSize:9.5,fontWeight:700,color:"#166534",background:"#DCFCE7",padding:"2px 6px",borderRadius:4}}>QUOTED AT CHECKOUT</span>
                            )}
                            {o.pickup_location && (
                              <span style={{fontSize:9.5,fontWeight:700,letterSpacing:"0.05em",color:"#0369A1",background:"#E0F2FE",padding:"2px 6px",borderRadius:4,textTransform:"uppercase"}}>
                                via {o.pickup_location}
                              </span>
                            )}
                          </div>
                          <div style={{fontSize:11,color:"#64748B",marginTop:2}}>
                            ETA ~{o.estimated_delivery_days} days
                            {o.etd ? ` · by ${o.etd}` : ""}
                            {o.rating ? ` · ⭐ ${Number(o.rating).toFixed(1)}` : ""}
                          </div>
                        </div>
                        <div style={{fontWeight:800,color:"#0F172A",fontSize:13,whiteSpace:"nowrap"}}>₹{Number(o.rate).toFixed(0)}</div>
                      </label>
                    );
                  })}
                </div>
                {quotedCourierId && selectedCourier && String(selectedCourier.courier_company_id) !== String(quotedCourierId) && (
                  <div data-testid="sr-courier-switch-warning" style={{marginTop:8,fontSize:11,color:"#92400E",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:6,padding:"6px 8px",lineHeight:1.5}}>
                    You switched away from the courier quoted at checkout ({quoted.courier || "quoted courier"}). The buyer's escrowed delivery charge was based on that courier.
                  </div>
                )}
                {escrowedRate > 0 && selectedCourier && Number(selectedCourier.rate) > escrowedRate && (
                  <div data-testid="sr-over-escrow-warning" style={{marginTop:8,fontSize:11,color:"#92400E",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:6,padding:"6px 8px",lineHeight:1.5}}>
                    This courier (₹{Number(selectedCourier.rate).toFixed(0)}) costs more than the delivery escrowed at checkout (₹{escrowedRate.toFixed(0)} for {job.price_breakdown?.delivery?.weight_kg || "?"} kg). The difference may be deducted from your payout.
                  </div>
                )}
                {courierOptions.length > 1 && new Set(courierOptions.map(o => o.pickup_location)).size > 1 && (
                  <div data-testid="sr-multi-hub-hint" style={{marginTop:8,fontSize:10.5,color:"#475569",lineHeight:1.5,padding:"6px 8px",background:"rgba(14,165,233,0.06)",borderRadius:6}}>
                    Multiple pickup hubs active — we picked the cheapest combo first. Switching hubs can trade cost for speed.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {ewbBlocked && (
          <div data-testid="dispatch-ewb-block" style={{gridColumn:"1 / -1",padding:"10px 12px",background:"#FFFBEB",border:"1px solid #FCD34D",borderRadius:8,fontSize:12,color:"#92400E",lineHeight:1.5}}>
            <b>E-way bill required.</b> This consignment (₹{Number(ewb.value_inr).toLocaleString("en-IN")} incl. GST) is above the ₹{Number(ewb.limit_inr).toLocaleString("en-IN")} limit. Close this window and share your vehicle / transporter details on the job page. GoPrintPack will generate the e-way bill.
          </div>
        )}
        {error && (
          <div data-testid="dispatch-error" style={{gridColumn:"1 / -1",padding:"10px 12px",background:"#FEE2E2",border:"1px solid #FCA5A5",borderRadius:8,fontSize:12,color:"#991B1B",lineHeight:1.5}}>
            {error}
          </div>
        )}

        <div className="form-actions">
          <button data-testid="dispatch-cancel-button" type="button" className="secondary-btn" onClick={close} disabled={submitting || checking}>Cancel</button>
          {mode === "shiprocket" && step === "form" ? (
            <button type="button" data-testid="sr-check-serviceability" className="primary-btn" onClick={checkServiceability}
              disabled={checking || !shiprocketReady || ewbBlocked}>
              {checking ? "Checking couriers…" : <>Check courier options <Truck size={15}/></>}
            </button>
          ) : (
            <button data-testid="dispatch-submit-button" className="primary-btn"
              disabled={submitting || ewbBlocked || (mode === "shiprocket" && (step !== "checked" || !selectedCourier))}>
              {submitting
                ? (mode === "shiprocket" ? "Creating shipment…" : "Marking dispatched…")
                : mode === "shiprocket"
                  ? <>Generate blind label & dispatch via {selectedCourier?.courier_name || "selected courier"}{selectedCourier?.pickup_location ? ` · ${selectedCourier.pickup_location}` : ""} <Truck size={15}/></>
                  : <>Mark dispatched <Truck size={15}/></>}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}

export function ProofModal({ job, close, refresh }) {
  const [proof, setProof] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [history, setHistory] = useState([]);
  useEffect(() => {
    axios.get(`${API}/jobs/${job.id}/proof`).then(r => setProof(r.data));
    // History fetch is best-effort — a 403 (wrong role) just hides the panel.
    axios.get(`${API}/jobs/${job.id}/proof-history`).then(r => setHistory(r.data?.events || [])).catch(() => setHistory([]));
  }, [job.id]);

  const submit = async (approved) => {
    await axios.post(`${API}/jobs/${job.id}/proof/approve`, { approved, feedback });
    await refresh();
    close();
  };
  const iconFor = (a) => a === "proof_approved" ? "✅" : a === "proof_rejected" ? "❌" : a === "proof_uploaded" ? "📤" : "•";
  return (
    <Modal title="Digital proof review" close={close}>
      <div className="proof-preview" data-testid="proof-preview">
        {proof?.proof_url && <img src={proof.proof_url} alt="Digital 3D proof" />}
        <div className="proof-caption">
          <b>{job.title}</b>
          <span>{proof?.mock ? "MOCK 3D soft-proof" : "3D soft-proof"} · Rotate, zoom, and inspect finishes</span>
        </div>
      </div>
      <label className="proof-feedback">Feedback (optional)
        <textarea data-testid="proof-feedback-input" value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Note any changes needed on colour, finish, or die-line…"/>
      </label>
      {history.length > 0 && (
        <div data-testid="proof-history-panel" style={{marginTop:12,padding:"12px 14px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8}}>
          <div style={{fontSize:11,fontWeight:800,color:"#334155",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
            <ClipboardCheck size={13}/> Proof audit log · {history.length} event{history.length === 1 ? "" : "s"}
            <span style={{fontWeight:600,color:"#64748B",textTransform:"none",letterSpacing:"normal",marginLeft:"auto"}}>Immutable</span>
          </div>
          <ol style={{listStyle:"none",padding:0,margin:0,display:"flex",flexDirection:"column",gap:6,maxHeight:220,overflowY:"auto"}}>
            {history.map((e) => (
              <li key={e.id} data-testid={`proof-history-row-${e.id}`} style={{display:"flex",gap:8,padding:"6px 8px",background:"#fff",border:"1px solid #E2E8F0",borderRadius:6,fontSize:12,lineHeight:1.5}}>
                <span style={{fontSize:14,flexShrink:0}}>{iconFor(e.action)}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{color:"#0F172A",fontWeight:600}}>
                    {e.action.replace("proof_", "").replace("_", " ")}
                    <span style={{color:"#64748B",fontWeight:500,marginLeft:6}}>by {e.actor_name || "unknown"} · {e.actor_role}</span>
                  </div>
                  {e.note && <div style={{color:"#475569",fontSize:11.5,marginTop:1,overflow:"hidden",textOverflow:"ellipsis"}}>"{e.note}"</div>}
                  <div style={{color:"#94A3B8",fontSize:10.5,marginTop:1}}>{new Date(e.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
      <div className="form-actions">
        <button data-testid="proof-reject-button" className="secondary-btn" onClick={() => submit(false)}>Request revisions</button>
        <button data-testid="proof-approve-button" className="primary-btn" onClick={() => submit(true)}>Approve proof <CheckCircle2 size={15}/></button>
      </div>
    </Modal>
  );
}
